import { useState, useEffect } from 'react';
import { getSyncLogs } from '../lib/api';
import { format, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { CheckCircle2, XCircle, RefreshCw, Trash2, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    loadLogs();
  }, [typeFilter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const type = typeFilter === 'all' ? undefined : typeFilter;
      const response = await getSyncLogs(100, type);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (isoString) => {
    try {
      return format(parseISO(isoString), "d. MMM yyyy HH:mm:ss", { locale: da });
    } catch {
      return isoString;
    }
  };

  const getLogTypeIcon = (type) => {
    switch (type) {
      case 'hubhus_sync':
        return <RefreshCw className="h-4 w-4" />;
      case 'gdpr_purge':
        return <Trash2 className="h-4 w-4" />;
      case 'push_sent':
        return <Bell className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getLogTypeBadge = (type) => {
    switch (type) {
      case 'hubhus_sync':
        return <Badge className="bg-blue-50 text-blue-700">Hubhus Sync</Badge>;
      case 'gdpr_purge':
        return <Badge className="bg-purple-50 text-purple-700">GDPR Purge</Badge>;
      case 'push_sent':
        return <Badge className="bg-teal-50 text-teal-700">Push Sent</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-outfit">Sync Logs</h1>
          <p className="text-slate-500 mt-1">Oversigt over systemets baggrundsjobs</p>
        </div>
        
        <Button variant="outline" onClick={loadLogs} data-testid="refresh-logs-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Opdater
        </Button>
      </div>

      {/* Filter */}
      <Card className="border-slate-100 mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Filtrer:</span>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-log-type">
                <SelectValue placeholder="Alle typer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle typer</SelectItem>
                <SelectItem value="hubhus_sync">Hubhus Sync</SelectItem>
                <SelectItem value="gdpr_purge">GDPR Purge</SelectItem>
                <SelectItem value="push_sent">Push Sent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="border-slate-100">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-500">Ingen logs fundet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Tidspunkt</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Detaljer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, index) => (
                  <TableRow key={index} data-testid={`log-row-${index}`}>
                    <TableCell className="font-mono text-sm text-slate-600">
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getLogTypeIcon(log.type)}
                        {getLogTypeBadge(log.type)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.success ? (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle2 className="h-4 w-4" />
                          Success
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-600">
                          <XCircle className="h-4 w-4" />
                          Failed
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {log.type === 'hubhus_sync' && log.success && (
                        <span>
                          {log.events_fetched} events, {log.new_bookings} nye, {log.updated_bookings} opdateret
                        </span>
                      )}
                      {log.type === 'gdpr_purge' && log.success && (
                        <span>{log.records_purged} records slettet</span>
                      )}
                      {log.error && (
                        <span className="text-red-600">{log.error}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogsPage;
